import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription, fromEvent } from 'rxjs';
import { take } from 'rxjs/operators';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { ConfirmDialogData } from '../../../shared/models/confirm-dialog.model';
import { AuthService } from './auth.service';

@Injectable()
export class InactivityService {
    private static readonly INACTIVITY_TIMEOUT_MS = 20 * 60 * 1000;
    private static readonly WARNING_OFFSET_MS = 5 * 60 * 1000;

    private activitySubscriptions = new Subscription();
    private loginStatusSubscription: Subscription;
    private warningDialogRef: MatDialogRef<ConfirmDialogComponent>;

    private warningTimerId: any;
    private logoutTimerId: any;
    private initialized = false;

    constructor(private authService: AuthService, private dialog: MatDialog) { }

    initialize(): void {
        if (this.initialized) {
            return;
        }

        this.initialized = true;
        this.bindActivityEvents();

        this.loginStatusSubscription = this.authService.getLoginStatusEvent()
            .subscribe(isLoggedIn => {
                if (isLoggedIn) {
                    this.restartTimers();
                }
                else {
                    this.stopTracking();
                }
            });

        if (this.authService.isLoggedIn) {
            this.restartTimers();
        }
    }

    private bindActivityEvents(): void {
        const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

        for (const eventName of activityEvents) {
            this.activitySubscriptions.add(
                fromEvent(document, eventName).subscribe(() => this.onUserActivity())
            );
        }
    }

    private onUserActivity(): void {
        if (!this.authService.isLoggedIn) {
            return;
        }

        // Once warning is shown, require explicit user choice from the dialog.
        if (this.warningDialogRef) {
            return;
        }

        this.restartTimers();
    }

    private restartTimers(): void {
        this.clearTimers();
        this.closeWarningDialog();

        this.warningTimerId = setTimeout(() => {
            this.showWarningDialog();
        }, InactivityService.INACTIVITY_TIMEOUT_MS - InactivityService.WARNING_OFFSET_MS);

        this.logoutTimerId = setTimeout(() => {
            this.performAutoLogout();
        }, InactivityService.INACTIVITY_TIMEOUT_MS);
    }

    private showWarningDialog(): void {
        if (!this.authService.isLoggedIn || this.warningDialogRef) {
            return;
        }

        const data: ConfirmDialogData = {
            title: 'Session Expiring Soon',
            message: 'You will be logged out in 5 minutes due to inactivity. Click Stay Logged In to continue your session.',
            confirmText: 'Stay Logged In',
            cancelText: 'Logout Now'
        };

        this.warningDialogRef = this.dialog.open(ConfirmDialogComponent, {
            width: '460px',
            disableClose: true,
            data,
            autoFocus: false
        });

        this.warningDialogRef.afterClosed().pipe(take(1)).subscribe(shouldStayLoggedIn => {
            this.warningDialogRef = null;

            if (shouldStayLoggedIn === true) {
                this.restartTimers();
            }
            else if (shouldStayLoggedIn === false) {
                this.performAutoLogout();
            }
        });
    }

    private performAutoLogout(): void {
        if (!this.authService.isLoggedIn) {
            this.stopTracking();
            return;
        }

        this.stopTracking();
        this.authService.logout();
        this.authService.redirectLogoutUser();
    }

    private stopTracking(): void {
        this.clearTimers();
        this.closeWarningDialog();
    }

    private clearTimers(): void {
        clearTimeout(this.warningTimerId);
        clearTimeout(this.logoutTimerId);

        this.warningTimerId = null;
        this.logoutTimerId = null;
    }

    private closeWarningDialog(): void {
        if (this.warningDialogRef) {
            this.warningDialogRef.close();
            this.warningDialogRef = null;
        }
    }
}
